import {
  IntegrationEndpointMinimal,
  IntegrationWebhookMinimal,
} from '@components/integrations/integrations-types';
import { getScopedTranslate } from '@locale/translate';

import { JourneyAction } from 'api/types';
import { getNodesToHide } from 'ui/journey-builder/hooks/use-collapsed-nodes';
import { nodeTypes } from 'ui/journey-builder/node-types/';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

const locale = {
  en: {
    configureAction: 'Configure action',
    noDescription: 'No description',
    journeyTriggers: 'Journey triggers',
  },
};

/**
 * Get the description for a journey action.
 * Returns a configuration prompt if not configured, the action's description if set,
 * or falls back to the node type's getDescription method.
 */
export const getDescription = (action: JourneyAction) => {
  const { t } = getScopedTranslate(locale);
  if (!action.isConfigured) {
    return t('configureAction');
  } else if (action.description) {
    return action.description;
  } else {
    const node = nodeTypes[action.action];
    return (
      (node &&
        'getDescription' in node &&
        typeof node.getDescription === 'function' &&
        node.getDescription(action)) ||
      t('noDescription')
    );
  }
};

type DescendantMaps = {
  descendantsCountMap: Record<string, number>;
  childrenMap: Record<string, JourneyAction[]>;
};

/**
 * Generate maps for action hierarchy:
 * - childrenMap: mapping of action shortId to its direct children
 * - descendantsCountMap: mapping of action shortId to total descendant count
 */
export const generateDescendantMaps = (actions: Array<JourneyAction>): DescendantMaps => {
  const childrenMap: Record<string, JourneyAction[]> = {};

  actions.forEach((action) => {
    const parentId = action.parentAction?.shortId;
    if (parentId) {
      if (!childrenMap[parentId]) {
        childrenMap[parentId] = [];
      }
      childrenMap[parentId]!.push(action);
    }
    if (!childrenMap[action.shortId]) {
      childrenMap[action.shortId] = [];
    }
  });

  const descendantsCountMap: Record<string, number> = {};

  const countDescendants = (action: JourneyAction): number => {
    if (descendantsCountMap[action.shortId] !== undefined) {
      return descendantsCountMap[action.shortId];
    }

    const children = childrenMap[action.shortId] || [];

    let count = 0;
    children.forEach((childAction) => {
      const childCountValue = childAction.action === 'branch_base' ? 0 : 1;
      count += childCountValue + countDescendants(childAction);
    });

    descendantsCountMap[action.shortId] = count;

    return descendantsCountMap[action.shortId];
  };

  actions.forEach((action) => {
    countDescendants(action);
  });

  return { descendantsCountMap, childrenMap };
};

/**
 * Collect every action that gets deleted with a branch, in DFS pre-order.
 * Skips branch_base lane rows (they are structural, not actions) but walks
 * through them, so nested journey_branch actions and lane children are
 * included. The result length always equals descendantsCountMap[branchShortId].
 */
export const collectBranchDescendants = (
  actions: Array<JourneyAction>,
  branchShortId: string
): JourneyAction[] => {
  const { childrenMap } = generateDescendantMaps(actions);
  const descendants: JourneyAction[] = [];
  const visit = (shortId: string) => {
    (childrenMap[shortId] ?? []).forEach((childAction) => {
      if (childAction.action !== 'branch_base') {
        descendants.push(childAction);
      }
      visit(childAction.shortId);
    });
  };
  visit(branchShortId);
  return descendants;
};

/**
 * Find an integration endpoint by ID from the list of available endpoints.
 */
export const getIntegrationEndpoint = (
  integrations: IntegrationEndpointMinimal[],
  action: JourneyAction<'integration'>
) => {
  const endpointId =
    action.values?.integrationEndpointId?.value ?? action.values?.templateEndpointId ?? '';
  return integrations.find((integration) => integration.shortId === endpointId);
};

/**
 * Find an integration webhook by ID from the list of available webhooks.
 */
export const getIntegrationWebhook = (
  webhooks: IntegrationWebhookMinimal[],
  action: JourneyAction<'webhook'>
) => {
  const webhookId =
    action.values?.integrationWebhookId?.value ?? action.values?.templateWebhookId ?? '';
  return webhooks.find((integration) => integration.shortId === webhookId);
};

export type ConvertToNodesProps = {
  actions: JourneyAction[];
  integrations: IntegrationEndpointMinimal[];
  webhooks: IntegrationWebhookMinimal[];
  collapsedActions: Array<JourneyAction['shortId']>;
  triggersCount: number;
  isTriggersCollapsed: boolean;
};

/**
 * Convert journey actions to React Flow nodes for rendering in the journey builder.
 * Handles node visibility, action hierarchy, and integration data.
 */
export const convertToNodes = ({
  actions,
  integrations,
  webhooks,
  collapsedActions,
  triggersCount,
  isTriggersCollapsed,
}: ConvertToNodesProps): JourneyNode[] => {
  const { t } = getScopedTranslate(locale);
  const nodes: JourneyNode[] = [];
  const nodesToHide = getNodesToHide({ actions, collapsedActions });
  const { descendantsCountMap, childrenMap } = generateDescendantMaps(actions);

  const getIsRemoveDisabled = (action: JourneyAction) => {
    if (action.action === 'journey_branch') {
      if (action.dependants.length > 1) {
        return true;
      }
      const children = childrenMap[action.shortId];
      if (children.length === 1) {
        return false;
      }
      const childrenWithChildren: string[] = [];
      children.forEach(({ shortId }) => {
        const childrensChildren = childrenMap[shortId];
        if (childrensChildren.length > 0) {
          childrenWithChildren.push(shortId);
        }
      });
      return childrenWithChildren.length > 1;
    }
    return false;
  };

  const journeyTriggersId = 'journey-triggers';

  if (actions.length > 0) {
    nodes.push({
      id: journeyTriggersId,
      type: 'journey_triggers',
      hidden: false,
      height: 700,
      dependantIds: [],
      data: {
        nodeDescription: t('journeyTriggers'),
        isConfigured: true,
        isCollapsed: false,
        values: { triggersCount, isTriggersCollapsed },
      },
    });
  }

  actions.forEach((action) => {
    const values = action.values ?? {};
    const nodeDescription = getDescription(action);
    const integration =
      action.action === 'integration'
        ? getIntegrationEndpoint(integrations, action as JourneyAction<'integration'>)
        : action.action === 'webhook'
        ? getIntegrationWebhook(webhooks, action as JourneyAction<'webhook'>)
        : undefined;
    const parentId =
      action.action === 'journey_start' ? journeyTriggersId : action.parentAction?.shortId;
    nodes.push({
      id: action.shortId,
      type: action.action,
      hidden: nodesToHide.has(action.shortId),
      parentId,
      ...(action.parentAction && {
        dependantIds: action.dependants,
      }),
      data: {
        values,
        description: action.description,
        nodeDescription,
        isAutomatic: action.isAutomatic,
        assignees: action.assignees,
        isConfigured: action.isConfigured,
        reminder: action.reminder,
        integration,
        isCollapsed: collapsedActions.includes(action.shortId),
        descendantCount: descendantsCountMap[action.shortId],
        childrenCount: childrenMap[action.shortId].length,
        isRemoveDisabled: getIsRemoveDisabled(action),
      },
    });
  });

  return nodes;
};
